import { useEffect, useState } from "react";
import api from "../api/api";
import SideNavLayout from "../customsidenav/sidenavlayout";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Backdrop,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ToastMessage from "../utils/mui/righttoptoast";
import { getPracticeId, practiceShortName } from "../config/practice";

/*
 * OSA's Online Inquiries (/webinquiry for features.webInquiry, 7825), ported
 * from its own build (webinquiry/webinquiry.jsx): the website contact-form
 * inquiries (active ones), with admin notes and a soft delete (status 0).
 * v1 website-inquiry/get, /{id}/update-notes, /{id}/update-status -- the v1
 * routes OSA calls, pinned to v1 (`_gw`) so a v2 routing rule never moves
 * them. 9928's website requests (CareIQ) are a different page.
 */
export const inquiryRoutes = (practiseid, id) => ({
  get: `/v1/${practiseid}/website-inquiry/get`,
  notes: `/v1/${practiseid}/website-inquiry/${id}/update-notes`,
  status: `/v1/${practiseid}/website-inquiry/${id}/update-status`,
});
const V1 = { _gw: "v1" };

// The rows OSA lists: each inquiry with its JSON `data` (first, last, phone,
// email, reason, notes) spread onto it, active ones (status 1) only.
export function activeInquiries(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((item) => {
      let parsedData = {};
      try {
        parsedData = typeof item.data === "string" ? JSON.parse(item.data) : item.data || {};
      } catch (error) {
        console.error("Invalid inquiry data for id", item.id);
      }
      return { ...item, ...parsedData };
    })
    .filter((item) => item.status === 1);
}

dayjs.extend(utc);
dayjs.extend(timezone);

const WebInquiry = () => {
  const practiseid = getPracticeId();

  const [allInquirys, setAllInquirys] = useState([]);
  const [filteredInquirys, setFilteredInquirys] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [search, setSearch] = useState("");

  /* ── View Message Modal ── */
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const openViewDialog = (item) => {
    setViewTarget(item);
    setViewDialogOpen(true);
  };
  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setViewTarget(null);
  };

  /* ── Notes Dialog ── */
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null);
  const [notesInput, setNotesInput] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  /* ── Delete Dialog ── */
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Toast ── */
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
  const handleToastClose = () => setOpenToast(false);
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  /* ── Fetch ── */
  const fetchdata = async () => {
    try {
      setLoading(true);
      const response = await api.get(inquiryRoutes(practiseid).get, V1);
      const data = activeInquiries(response?.data);

      setAllInquirys(data);
      setFilteredInquirys(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  /* ── Helpers ── */
  const applyItemUpdate = (id, updates) => {
    fetchdata();
    const updateList = (list) =>
      list.map((item) => (item.id === id ? { ...item, ...updates } : item));
    setAllInquirys(updateList);
    setFilteredInquirys(updateList);
  };

  const openNotesDialog = (item) => {
    setNotesTarget(item);
    setNotesInput(item.admin_notes || "");
    setNotesDialogOpen(true);
  };

  const closeNotesDialog = () => {
    setNotesDialogOpen(false);
    setNotesTarget(null);
    setNotesInput("");
  };

  const submitNotes = async () => {
    if (!notesTarget) return;
    try {
      setSavingNotes(true);
      await api.put(inquiryRoutes(practiseid, notesTarget.id).notes, { admin_notes: notesInput }, V1);
      applyItemUpdate(notesTarget.id, { admin_notes: notesInput });
      showToast("Notes updated successfully!");
      closeNotesDialog();
    } catch (error) {
      console.log(error);
      showToast("Failed to update notes", "error");
    } finally {
      setSavingNotes(false);
    }
  };

  const updateStatus = async (item, newStatus) => {
    try {
      setStatusUpdatingId(item.id);
      await api.put(inquiryRoutes(practiseid, item.id).status, { status: newStatus }, V1);
      applyItemUpdate(item.id, { status: newStatus });
      showToast(`Marked as ${newStatus === 1 ? "Active" : "Inactive"}`);
    } catch (error) {
      console.log(error);
      showToast("Failed to update status", "error");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const openDeleteDialog = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await updateStatus(deleteTarget, 0);
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    document.title = `${practiceShortName()} - Online Inquiries`;
    fetchdata();
  }, []);

  /* ── Search filter ── */
  useEffect(() => {
    const filtered = allInquirys.filter((item) => {
      const q = search.toLowerCase();
      return (
        item.phone?.toString().toLowerCase().includes(q) ||
        item.first?.toLowerCase().includes(q) ||
        item.last?.toLowerCase().includes(q) ||
        item.email?.toLowerCase().includes(q)
      );
    });
    setFilteredInquirys(filtered);
    setPage(0);
  }, [search, allInquirys]);

  const resetFilters = () => {
    setSearch("");
    setPage(0);
  };

  /* ── Paginated rows ── */
  const displayRows = filteredInquirys.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <SideNavLayout>
      <div className="w-full">

        {/* ── Filter Row (same style as Logs) ── */}
        <div className="w-full flex items-center justify-between gap-4 mb-3 font-inter">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-slate-700 font-inter">
              Online Inquiries
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative w-64">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"
              />
              <input
                type="text"
                placeholder="Search name, phone, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs font-inter bg-white border border-slate-200 rounded-xl outline-none focus:border-[#1D2951] shadow-2xs placeholder:text-slate-400"
              />
            </div>

            <button
              onClick={resetFilters}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold font-inter transition-all cursor-pointer shadow-2xs whitespace-nowrap active:scale-[0.98]"
            >
              RESET FILTERS
            </button>

            <button
              onClick={() => fetchdata()}
              className="px-4 py-2 rounded-xl bg-[#1D2951] hover:bg-[#162040] text-white text-xs font-semibold font-inter shadow-2xs transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap active:scale-[0.98]"
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
              REFRESH
            </button>
          </div>
        </div>

        {/* ── Table (same Paper/Table pattern as Logs) ── */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "none",
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredInquirys.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />

          <TableContainer sx={{ maxHeight: "75vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Phone / Email
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Reason
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Message
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Admin Notes
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", color: "#475569", py: 1.5 }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {displayRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 6, color: "#64748B" }}
                    >
                      No inquiries found.
                    </TableCell>
                  </TableRow>
                ) : (
                  displayRows.map((item, idx) => (
                    <TableRow
                      key={item.id || idx}
                      hover
                      sx={{ "& > td": { verticalAlign: "top", py: 1.25 } }}
                    >
                      {/* Name */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <div className="text-xs font-semibold text-slate-800 font-inter">
                          {item.first} {item.last}
                        </div>
                      </TableCell>

                      {/* Phone / Email */}
                      <TableCell>
                        <div className="text-xs font-semibold text-slate-800 font-inter">
                          {item.phone || "—"}
                        </div>
                        {item.email && (
                          <div className="text-[11px] text-slate-400 font-inter mt-0.5">
                            {item.email}
                          </div>
                        )}
                      </TableCell>

                      {/* Reason */}
                      <TableCell>
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-md font-inter inline-block">
                          {item.reason || "—"}
                        </span>
                      </TableCell>

                      {/* Message — View button */}
                      <TableCell align="left">
                        {item.notes ? (
                          <button
                            onClick={() => openViewDialog(item)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold font-inter transition-all active:scale-95"
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                            View
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 font-inter">—</span>
                        )}
                      </TableCell>

                      {/* Admin Notes preview (truncated) */}
                      <TableCell>
                        {item.admin_notes ? (
                          <div
                            className="text-xs text-slate-600 font-inter leading-relaxed max-w-[180px] truncate"
                            title={item.admin_notes}
                          >
                            {item.admin_notes}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 font-inter">—</span>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell align="center">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border font-inter ${
                            item.status === 0
                              ? "text-[#4A5565] bg-[#F1F5F9] border-[#99A1AF]"
                              : "text-[#22C55E] bg-[#F0FDF4] border-[#22C55E]"
                          }`}
                        >
                          {item.status === 0 ? "Inactive" : "Active"}
                        </span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <div className="flex items-center justify-center gap-0.5">
                          <Tooltip title="Edit admin notes" arrow>
                            <IconButton
                              size="small"
                              onClick={() => openNotesDialog(item)}
                              sx={{ color: "#3B82F6", "&:hover": { bgcolor: "#EFF6FF" } }}
                            >
                              <EditNoteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              disabled={statusUpdatingId === item.id}
                              onClick={() => openDeleteDialog(item)}
                              sx={{
                                color: "#EF4444",
                                "&:hover": { bgcolor: "#FEF2F2" },
                                "&.Mui-disabled": { opacity: 0.4 },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>

      {/* ── Loader ── */}
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* ── View Message Modal ── */}
      <Dialog
        open={viewDialogOpen}
        onClose={closeViewDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "0.95rem", pb: 1 }}>
          Message
          {viewTarget
            ? ` — ${viewTarget.first || ""} ${viewTarget.last || ""}`.trim()
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {/* Enquiry meta */}
          {viewTarget && (
            <div className="mb-4 flex flex-wrap gap-3">
              {viewTarget.phone && (
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                  <span className="text-[11px] font-semibold text-slate-500 font-inter">Phone:</span>
                  <span className="text-xs text-slate-800 font-inter">{viewTarget.phone}</span>
                </div>
              )}
              {viewTarget.email && (
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                  <span className="text-[11px] font-semibold text-slate-500 font-inter">Email:</span>
                  <span className="text-xs text-slate-800 font-inter">{viewTarget.email}</span>
                </div>
              )}
              {viewTarget.reason && (
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                  <span className="text-[11px] font-semibold text-blue-500 font-inter">Reason:</span>
                  <span className="text-xs text-blue-800 font-inter">{viewTarget.reason}</span>
                </div>
              )}
            </div>
          )}

          {/* Message body */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 font-semibold font-inter mb-2 uppercase tracking-wider">
              Message
            </p>
            <p className="text-sm text-slate-700 font-inter leading-relaxed whitespace-pre-wrap">
              {viewTarget?.notes || "No message provided."}
            </p>
          </div>

          {/* Admin notes (read-only preview in view modal) */}
          {viewTarget?.admin_notes && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs text-amber-500 font-semibold font-inter mb-2 uppercase tracking-wider">
                Admin Notes
              </p>
              <p className="text-sm text-slate-700 font-inter leading-relaxed whitespace-pre-wrap">
                {viewTarget.admin_notes}
              </p>
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeViewDialog} variant="outlined" size="small">
            Close
          </Button>
          <Button
            onClick={() => {
              closeViewDialog();
              openNotesDialog(viewTarget);
            }}
            variant="contained"
            size="small"
            startIcon={<EditNoteIcon />}
          >
            Edit Admin Notes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Notes Dialog ── */}
      <Dialog
        open={notesDialogOpen}
        onClose={closeNotesDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          Admin Notes
          {notesTarget
            ? ` — ${notesTarget.first || ""} ${notesTarget.last || ""}`.trim()
            : ""}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            placeholder="Enter admin notes..."
            value={notesInput}
            onChange={(e) => setNotesInput(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeNotesDialog} disabled={savingNotes}>
            Cancel
          </Button>
          <Button
            onClick={submitNotes}
            variant="contained"
            disabled={savingNotes}
          >
            {savingNotes ? "Saving..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          Delete Inquiry
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete
          {deleteTarget
            ? ` ${deleteTarget.first || ""} ${deleteTarget.last || ""}`
            : " this"}
          's inquiry? This will mark it as inactive.
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastMessage
        open={openToast}
        message={toastMessage}
        severity={toastSeverity}
        handleClose={handleToastClose}
      />
    </SideNavLayout>
  );
};

export default WebInquiry;